'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useUpdateUser, useUser } from '@/hooks/use-users'
import { UserForm } from '@/components/partials/users'
import { type UserFormRef } from '@/components/partials/users/form'
import { ContentError, FormSkeleton } from '@/components/partials'
import { UpdateUserParams } from '@/types/user'
import { FormModeEnum } from '@/enums'

interface UpdateProps {
  id: string
}

const Update = ({ id }: UpdateProps) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const userFormRef = useRef<UserFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: user, isLoading, error } = useUser(id)
  const { mutate: updateUser, isPending } = useUpdateUser()

  const handleSubmit = (data: UpdateUserParams) => {
    updateUser(
      { userId: id, params: data },
      {
        onSuccess: async updatedUser => {
          setIsRedirecting(true)
          queryClient.setQueryData(['user', id], updatedUser)
          await queryClient.invalidateQueries({ queryKey: ['users'] })
          toast.success(t('updated_successfully'))
          router.push(`/users/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    console.error('Error updating user:', error)

    // Handle validation errors
    if (userFormRef.current) {
      userFormRef.current.handleValidationErrors(error)
    }

    // Show error property for non-validation errors
    if (error.response?.status !== 422) {
      toast.error(t('error_updating'))
    }
  }

  const handleCancel = () => {
    router.push(`/users/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={3} showTranslations={false} />
  }

  if (error || !user) {
    return <ContentError error={error} listUrl="/users" />
  }

  return (
    <UserForm
      ref={userFormRef}
      mode={FormModeEnum.UPDATE}
      user={user}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
